import React, { useEffect, useRef, useState } from 'react';
import { request } from '../../fetch/core';
import {
  enumTranslator,
  isEmpty,
  isNullObject,
  transformLocationWgs84togcj02,
  translatorVendingScheduleState,
} from '../../utils';
import { GlobalEventName } from '../../utils/constant';
import {
  addGlobalEventListener,
  removeGlobalEventListener,
} from '../../utils/emit';
import { init2DMap } from '../../utils/initMap';
import CustomOverlay from './CustomOverlay';
import ClusterIcon from '../../assets/img/views/map/cluster.png';
import {
  stationImg,
  carNormal1,
  carNormal2,
  carAlarm1,
  carAlarm2,
  stopIcon,
  endPoint,
  getOffsetTop,
  getCityIcon,
  getCityIconSize,
} from './Icon';
import './index.scss';
import { VehicleScheduleState } from '../../redux/type';

const winAny = window as any;

const toBD09 = (lat: number | string, lon: number | string) => {
  if (winAny.coordtransform) {
    try {
      const bd = winAny.coordtransform().gcj02tobd09(parseFloat(lon as string), parseFloat(lat as string));
      return { lat: bd[1], lon: bd[0] };
    } catch (e) {
      console.warn('[MapMode] coordtransform failed, using original coords', e);
    }
  }
  return { lat: parseFloat(lat as string), lon: parseFloat(lon as string) };
};

function handleJumpToSinglePage(license: string) {
  const url = '/app/single?license=' + license;
  window.open(url, '_blank');
}

winAny.handleJumpToSinglePage = handleJumpToSinglePage;

const Map = (props: any) => {
  const { mapData, isBeiDou } = props;
  const [isShowZoom, setIsShowZoom] = useState(props?.showZoomInfo || false);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>({
    cityMarker: {},
    routingPathMarker: {},
    planningRoutingPath: {},
    stopMarker: {},
    stationMarker: {},
    vehiclePositionMarker: {},
    finishedRoutingPath: {},
  });
  const markerClustererRef = useRef<any>();
  const cityOverlayRef = useRef<any>();
  const zoomInfoDivRef = useRef<HTMLDivElement | null>(null);
  const stopTooltipRef = useRef<any>(null);

  const ensureMap = () => {
    if (mapRef.current) {
      console.log('[MapMode] ensureMap: map already exists');
      return true;
    }
    const { BMapGL } = winAny;
    const mapContainer = containerRef.current;
    console.log('[MapMode] ensureMap: BMapGL=', !!BMapGL, 'mapContainer=', !!mapContainer);
    if (BMapGL && mapContainer) {

      const { lat: bdLat, lon: bdLon } = toBD09(34.53506, 108.93885);
      mapRef.current = init2DMap(
        { lat: bdLat, lon: bdLon },
        mapContainer,
        mapData.zoom || 5,
      );
      console.log('[MapMode] ensureMap: init result=', !!mapRef.current);
    }
    return !!mapRef.current;
  };

  const showZoomInfo = () => {
    if (zoomInfoDivRef.current || !containerRef.current) return;
    const customDiv = document.createElement('div');
    customDiv.style.cssText =
      'position:absolute;bottom:10px;right:10px;padding:5px;width:140px;height:50px;line-height:50px;text-align:center;font-size:14px;color:white;z-index:1000;pointer-events:none;';
    function update() {
      const currentZoom = mapRef.current.getZoom();
      customDiv.innerHTML = '当前地图层级：' + currentZoom;
    }
    update();
    mapRef.current.addEventListener('zoomend', update);
    containerRef.current.appendChild(customDiv);
    zoomInfoDivRef.current = customDiv;
  };

  const addCityOverlay = (
    name: string,
    point: any,
    size: string,
    detail?: any,
  ) => {
    const overlayDom =
      '<div class="overlay-container">' +
      `<div class="city"><span class="city-name">${name}</span></div>` +
      `${
        detail && detail.stationNum && detail.total
          ? `<div class='station-vehicle'><span class='font-JDZhengHT title'>${detail.stationNum}</span><span class='font-JDZhengHT sub-title'>营业部</span><span class="divider"></span><span class='font-JDZhengHT title'>${detail.total}</span><span class='font-JDZhengHT sub-title'>车辆</span></div>`
          : ''
      }` +
      '</div>';

    const overlay: any = new CustomOverlay(
      overlayDom,
      point,
      getOffsetTop(size),
      12,
    );
    mapRef.current && mapRef.current.addOverlay(overlay);
    return overlay;
  };

  const addVehicleOverlay = (
    name: string,
    point: any,
    detail?: any,
    offsetTop?: number,
  ) => {
    const overlayDom =
      '<div class="overlay-container">' +
      `<span class="label-text">${name}</span>` +
      `${
        detail && detail.warning && detail.text
          ? "<span class='alarm-text'>" + detail.text + '</span>'
          : ''
      }`;
    ('</div>');
    const overlay: any = new CustomOverlay(overlayDom, point, offsetTop || 0);
    mapRef.current && mapRef.current.addOverlay(overlay);
    return overlay;
  };

  const addVehicleClusterOverlay = (
    point: any,
    detail?: {
      name: string;
      warning?: boolean;
      text?: string;
    }[],
    offsetTop?: number,
    zIndex?: number,
  ) => {
    let overlayDom = '<div class="overlay-container">';
    if (!isEmpty(detail)) {
      detail?.forEach(
        (item: { name: string; warning?: boolean; text?: string }) => {
          const returnDomStr = (item: any) => {
            const { name, warning, text } = item;
            let tmpStr = `<div onclick="handleJumpToSinglePage('${name}')" style="cursor:pointer">
                          <span class="label-text">${name}</span>`;
            if (warning && text) {
              tmpStr += `<span class='alarm-text'>${text}</span>`;
            }
            tmpStr += '</div>';
            return tmpStr;
          };
          overlayDom += returnDomStr(item);
        },
      );
    }
    overlayDom += '</div>';
    const overlay: any = new CustomOverlay(
      overlayDom,
      point,
      offsetTop || 0,
      zIndex,
    );
    mapRef.current && mapRef.current.addOverlay(overlay);
    return overlay;
  };

  const handleClickCityLevel = (e: any) => {
    props.handleClickCityLevel && props.handleClickCityLevel(e.target.markerId);
  };

  const handleClickVehicleMarker = (e: any) => {
    const { markerId } = e.target;
    const url = '/app/single?license=' + markerId;
    window.open(url, '_blank');
  };

  const handleClickStationIcon = (e: any) => {
    props.handleClickStationIcon &&
      props.handleClickStationIcon(e.target.markerId);
  };

  const clearCityMarker = () => {
    const homePoints: any = Object.values(markerRef.current.cityMarker);
    for (const cityMarker of homePoints) {
      cityMarker.marker && mapRef.current.removeOverlay(cityMarker.marker);
      cityMarker.overlay && mapRef.current.removeOverlay(cityMarker.overlay);
    }
    markerRef.current.cityMarker = {};
  };
  const clearCityOverlay = () => {
    const homePoints: any = Object.values(markerRef.current.cityMarker);
    for (const cityMarker of homePoints) {
      cityMarker.overlay && mapRef.current.removeOverlay(cityMarker.overlay);
    }
  };
  const clearStationMarker = () => {
    const homePoints: any = Object.values(markerRef.current.stationMarker);
    for (const stationMarker of homePoints) {
      stationMarker.marker && mapRef.current.removeOverlay(stationMarker.marker);
      stationMarker.overlay && mapRef.current.removeOverlay(stationMarker.overlay);
    }
    markerRef.current.stationMarker = {};
  };

  const clearVehicleMarkers = () => {
    const vehicleMakers: any = Object.values(markerRef.current.vehiclePositionMarker);
    for (const vMarker of vehicleMakers) {
      vMarker.marker && mapRef.current.removeOverlay(vMarker.marker);
      vMarker.overlay && mapRef.current.removeOverlay(vMarker.overlay);
    }
    // 销毁聚类实例
    if (markerClustererRef.current && markerClustererRef.current.destroy) {
      markerClustererRef.current.destroy();
    }
    markerClustererRef.current = null;
  };

  const clearStopMarkers = () => {
    hideTipsInfo();
    const stopPoints: any = Object.values(markerRef.current.stopMarker);
    for (const stop of stopPoints) {
      stop.forEach((item: any) => {
        item.marker && mapRef.current.removeOverlay(item.marker);
      });
    }
    markerRef.current.stopMarker = {};
  };

  const clearTotalRoutingPath = () => {
    const totalPathMarker: any = Object.entries(markerRef.current.routingPathMarker);
    for (const [vehicleName, v] of totalPathMarker) {
      v.forEach((item: any) => mapRef.current.removeOverlay(item));
    }
    markerRef.current.routingPathMarker = {};
  };

  const clearFinishedRoutingPath = () => {
    const finishedPathMarker: any = Object.entries(markerRef.current.finishedRoutingPath);
    for (const [vehicleName, v] of finishedPathMarker) {
      v.forEach((item: any) => mapRef.current.removeOverlay(item));
    }
    markerRef.current.finishedRoutingPath = {};
  };

  const clearPlanningRoutingPath = () => {
    const planningPathMarker: any = Object.entries(markerRef.current.planningRoutingPath);
    for (const [vehicleName, v] of planningPathMarker) {
      v.forEach((item: any) => mapRef.current.removeOverlay(item));
    }
    markerRef.current.planningRoutingPath = {};
  };

  const showCityPosition = () => {
    if (!ensureMap()) return;
    clearCityMarker();
    const { BMapGL } = winAny;
    const { city } = mapData;
    if (isNullObject(city)) {
      return;
    }
    const cityArr = Object.values(city);
    for (const cityItem of cityArr) {
      const { id, name, total, stationNum, lat, lon }: any = cityItem;
      if (!lat || !lon) continue;
      const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
      const point = new BMapGL.Point(bdLon, bdLat);
      const iconPath = getCityIcon(total);
      const iconSize = getCityIconSize(iconPath);
      const size = new BMapGL.Size(iconSize.width, iconSize.height);
      const homeMarker = new BMapGL.Marker(point, {
        icon: new BMapGL.Icon(iconPath, size, {
          anchor: new BMapGL.Size(iconSize.width / 2, iconSize.height),
        }),
        title: name,
      });
      homeMarker.markerId = id;
      homeMarker.markerType = 1;
      homeMarker.setZIndex(10);
      mapRef.current.addOverlay(homeMarker);
      markerRef.current.cityMarker[id] = {
        marker: homeMarker,
      };

      homeMarker.addEventListener('click', function (e: any) {
        handleClickCityLevel(e);
      });

      homeMarker.addEventListener('mouseover', function (e: any) {
        clearCityOverlay();
        cityOverlayRef.current = addCityOverlay(name, point, iconPath, {
          total: total,
          stationNum: stationNum,
        });
        markerRef.current.cityMarker[id] = {
          ...markerRef.current.cityMarker[id],
          overlay: cityOverlayRef.current,
        };
      });

      homeMarker.addEventListener('mouseout', function (e: any) {
        clearCityOverlay();
      });
    }

    if (!isEmpty(cityArr)) {
      const { lat, lon }: any = cityArr[0];
      const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
      mapRef.current && mapRef.current.setCenter(
        new BMapGL.Point(bdLon, bdLat),
      );
    }
  };

  const showStationPosition = () => {
    if (!ensureMap()) {
      console.log('[MapMode] showStationPosition: ensureMap failed');
      return;
    }
    clearStationMarker();
    const { BMapGL } = winAny;
    const { station } = mapData;
    const stationArr = Object.values(station);
    for (const stationItem of stationArr) {
      const { lat, lon, name, id, tipsInfo }: any = stationItem;
      if (!lat || !lon) {
        continue;
      }
      const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
      let point;
      try {
        point = new BMapGL.Point(bdLon, bdLat);
      } catch (e) {
        console.error('[MapMode] showStationPosition: BMapGL.Point error', e);
        continue;
      }
      const size = new BMapGL.Size(40, 44);
      const icon = new BMapGL.Icon(stationImg, size, {
        anchor: new BMapGL.Size(20, 44),
      });
      const homeMarker = new BMapGL.Marker(point, {
        icon,
        title: tipsInfo || name,
      });
      homeMarker.markerId = id;
      homeMarker.markerType = 1;
      homeMarker.setZIndex(10);
      try {
        mapRef.current.addOverlay(homeMarker);
        console.log('[MapMode] showStationPosition: addOverlay success for', name);
      } catch (e) {
        console.error('[MapMode] showStationPosition: addOverlay error', e);
      }
      const overlay = addVehicleOverlay(name, point);
      markerRef.current.stationMarker[id] = {
        marker: homeMarker,
        overlay,
      };
      homeMarker.addEventListener('click', handleClickStationIcon);
    }

    if (!isEmpty(stationArr)) {
      const { lat, lon }: any = stationArr[0];
      const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
      mapRef.current && mapRef.current.setCenter(
        new BMapGL.Point(bdLon, bdLat),
      );
    }
  };

  const showVehicleRealTimePosition = () => {
    if (!ensureMap()) {
      console.log('[MapMode] showVehicleRealTimePosition: ensureMap failed');
      return;
    }
    clearVehicleMarkers();
    const { vehicle } = mapData;
    const { BMapGL } = winAny;
    const clusterPoints: any[] = [];

    for (const vehicleItem of Object.values(vehicle)) {
      const {
        lat,
        lon,
        name,
        hasAlarmEvent,
        vehicleBusinessType,
        scheduleState,
        systemState,
        taskType,
      }: any = vehicleItem;
      if (!lat || !lon) {
        console.log('[MapMode] showVehicleRealTimePosition: skip due to empty lat/lon');
        continue;
      }
      const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
      let point;
      try {
        point = new BMapGL.Point(bdLon, bdLat);
      } catch (e) {
        console.error('[MapMode] showVehicleRealTimePosition: BMapGL.Point error', e);
        continue;
      }
      const size = new BMapGL.Size(40, 44);
      let ico = '';
      if (vehicleBusinessType === 'VENDING') {
        if (hasAlarmEvent || systemState === 'CONNECTION_LOST') {
          ico = carAlarm2;
        } else {
          ico = carNormal2;
        }
      } else {
        if (hasAlarmEvent || systemState === 'CONNECTION_LOST') {
          ico = carAlarm1;
        } else {
          ico = carNormal1;
        }
      }
      const icon = new BMapGL.Icon(ico, size, {
        anchor: new BMapGL.Size(20, 44),
      });

      const vehicleStatus =
        systemState === 'CONNECTION_LOST'
          ? '失联'
          : enumTranslator('ALARM_EVENT_ENUM', hasAlarmEvent?.alarmEvent);
      const scheduleLabel =
        vehicleBusinessType === 'VENDING'
          ? translatorVendingScheduleState(scheduleState)
          : taskType === 'DROPOFF' && scheduleState === 'DELIVERY'
            ? VehicleScheduleState.DELIVERY
            : taskType === 'DROPOFF' && scheduleState === 'ALREADY'
              ? VehicleScheduleState.ALREADY
              : enumTranslator('SCHEDULE_STATE_ENUM', scheduleState);
      const overlay = addVehicleOverlay(name, point, {
        warning: hasAlarmEvent?.alarmEvent || systemState === 'CONNECTION_LOST',
        text: scheduleState ? vehicleStatus + '/' + scheduleLabel : '',
      });

      const vehicleMarker = new BMapGL.Marker(point, { icon });
      vehicleMarker.markerId = name;
      vehicleMarker.markerType = 2; // 车辆
      vehicleMarker.setZIndex(1);
      vehicleMarker.warning =
        !!hasAlarmEvent?.alarmEvent || systemState === 'CONNECTION_LOST';
      vehicleMarker.text = scheduleState
        ? vehicleStatus + '/' + scheduleLabel
        : '';
      vehicleMarker.lat = lat;
      vehicleMarker.lon = lon;
      try {
        mapRef.current.addOverlay(vehicleMarker);
        console.log('[MapMode] showVehicleRealTimePosition: addOverlay success for', name);
      } catch (e) {
        console.error('[MapMode] showVehicleRealTimePosition: addOverlay error', e);
      }
      markerRef.current.vehiclePositionMarker[name] = {
        marker: vehicleMarker,
        overlay,
      };

      vehicleMarker.addEventListener('click', handleClickVehicleMarker);

      // 为 cluster 插件准备 GeoJSON 数据
      clusterPoints.push({
        type: 'Feature',
        properties: {
          markerId: name,
          warning: vehicleMarker.warning,
          text: vehicleMarker.text,
        },
        geometry: {
          type: 'Point',
          coordinates: [lon, lat],
        },
      });
    }

    // 初始化聚类（只有多车时才启用，避免单车场景下出现默认红色 marker）
    if (clusterPoints.length > 1 && winAny.Cluster) {
      const { Cluster } = winAny;
      const cluster = new Cluster.View(mapRef.current, {
        clusterMinPoints: 2,
        clusterRadius: 20,
        clusterMaxZoom: 18,
        fitViewOnClick: false,
        renderClusterStyle: {
          type: Cluster.ClusterRender.DOM,
          inject: (data: any) => {
            const div = document.createElement('div');
            const count = data.pointCount || 0;
            div.style.cssText =
              'width:60px;height:60px;background:url(' +
              ClusterIcon +
              ') no-repeat center/contain;display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;font-weight:bold;pointer-events:none;';
            div.innerHTML =
              '<font style="color:#fff;font-size:16px;font-weight:bold;">' +
              count +
              '</font>';
            return div;
          },
        },
      });
      cluster.setData(clusterPoints);

      cluster.on(Cluster.ClusterEvent.CLICK, (e: any) => {
        if (!e.isCluster) return;
        // 获取聚合中的所有子点
        const leaves = cluster.getLeaves(e.id) || [];
        const detail = leaves.map((item: any) => ({
          warning: item.properties?.warning,
          name: item.properties?.markerId,
          text: item.properties?.text,
        }));
        if (detail.length > 0 && e.latLng) {
          const { lat: bdLat, lon: bdLon } = toBD09(e.latLng[1], e.latLng[0]);
          const overlay = addVehicleClusterOverlay(
            new BMapGL.Point(bdLon, bdLat),
            detail,
            -60,
            99,
          );
          setTimeout(() => {
            overlay && mapRef.current.removeOverlay(overlay);
          }, 10000);
        }
      });

      markerClustererRef.current = cluster;
    }
  };

  const showRoutingPath = () => {
    console.log('[MapMode] showRoutingPath called, finishedRoutingPoint=', mapData.finishedRoutingPoint);
    if (!ensureMap()) {
      console.log('[MapMode] showRoutingPath: ensureMap failed');
      return;
    }
    const { BMapGL } = winAny;
    clearFinishedRoutingPath();
    const { finishedRoutingPoint } = mapData;
    for (const vehicleName in finishedRoutingPoint) {
      if (finishedRoutingPoint[vehicleName]) {
        const pathArr: any = [];
        const routingPoint = finishedRoutingPoint[vehicleName] || [];
        routingPoint.forEach((i: any) => {
          const lon = parseFloat(i.lon);
          const lat = parseFloat(i.lat);
          if (!isNaN(lon) && !isNaN(lat)) {
            const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
            pathArr.push(new BMapGL.Point(bdLon, bdLat));
          }
        });
        if (pathArr.length < 2) {
          continue;
        }
        const path = new BMapGL.Polyline(pathArr, {
          strokeColor: '#00B2FF',
          strokeWeight: 3,
          zIndex: 12,
        });

        if (path) {
          mapRef.current.addOverlay(path);
          if (markerRef.current.finishedRoutingPath[vehicleName]) {
            markerRef.current.finishedRoutingPath[vehicleName].push(path);
          } else {
            markerRef.current.finishedRoutingPath[vehicleName] = [path];
          }
        }
      }
    }
  };

  const showTotalRoutingPath = () => {
    console.log('[MapMode] showTotalRoutingPath called, totalRoutingPoint=', mapData.totalRoutingPoint);
    if (!ensureMap()) {
      console.log('[MapMode] showTotalRoutingPath: ensureMap failed');
      return;
    }
    const { BMapGL } = winAny;
    const { totalRoutingPoint } = mapData;
    clearTotalRoutingPath();
    for (const vehicleName in totalRoutingPoint) {
      if (totalRoutingPoint[vehicleName]) {
        const routingPoint = totalRoutingPoint[vehicleName].filter(
          (item: any) => Array.isArray(item),
        );
        routingPoint.forEach((point: any) => {
          const pathArr: any = [];
          point.forEach((i: any) => {
            const lon = parseFloat(i.lon);
            const lat = parseFloat(i.lat);
            if (!isNaN(lon) && !isNaN(lat)) {
              const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
              pathArr.push(new BMapGL.Point(bdLon, bdLat));
            }
          });
          if (pathArr.length < 2) {
            return;
          }
          const path = new BMapGL.Polyline(pathArr, {
            strokeColor: '#D3E4FF',
            strokeWeight: 3,
            zIndex: 10,
          });
          if (path) {
            mapRef.current.addOverlay(path);
            markerRef.current.routingPathMarker[vehicleName] = [path];
          }
        });
      }
    }
  };

  const showPlanningRoutingPath = () => {
    console.log('[MapMode] showPlanningRoutingPath called, currentRoutingPoint=', mapData.currentRoutingPoint);
    if (!ensureMap()) {
      console.log('[MapMode] showPlanningRoutingPath: ensureMap failed');
      return;
    }
    const { BMapGL } = winAny;
    clearPlanningRoutingPath();
    const { currentRoutingPoint } = mapData;
    for (const vehicleName in currentRoutingPoint) {
      if (currentRoutingPoint[vehicleName]) {
        const routingPoint = currentRoutingPoint[vehicleName].filter(
          (item: any) => Array.isArray(item),
        );
        routingPoint.forEach((point: any) => {
          const pathArr: any = [];
          point.forEach((i: any) => {
            const lon = parseFloat(i.lon);
            const lat = parseFloat(i.lat);
            if (!isNaN(lon) && !isNaN(lat)) {
              const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
              pathArr.push(new BMapGL.Point(bdLon, bdLat));
            }
          });
          if (pathArr.length < 2) {
            return;
          }
          const path = new BMapGL.Polyline(pathArr, {
            strokeColor: '#A0A0A0',
            strokeWeight: 2,
            zIndex: 9,
          });
          if (path) {
            mapRef.current.addOverlay(path);
            if (markerRef.current.planningRoutingPath[vehicleName]) {
              markerRef.current.planningRoutingPath[vehicleName].push(path);
            } else {
              markerRef.current.planningRoutingPath[vehicleName] = [path];
            }
          }
        });
      }
    }
  };

  const showStopsPosition = () => {
    console.log('[MapMode] showStopsPosition called, stop=', mapData.stop);
    if (!ensureMap()) {
      console.log('[MapMode] showStopsPosition: ensureMap failed');
      return;
    }
    clearStopMarkers();
    const { BMapGL } = winAny;
    const { stop } = mapData;
    console.log('[MapMode] showStopsPosition: stop keys=', Object.keys(stop || {}));
    for (const vehicleName in stop) {
      if (stop[vehicleName]) {
        const stopValue = stop[vehicleName];
        markerRef.current.stopMarker[vehicleName] = [];
        for (const point of Object.values(stopValue)) {
          const { lat, lon, name, id, status, stopAction, goalId }: any = point;
          console.log('[MapMode] showStopsPosition: stopItem=', { lat, lon, name, id, stopAction });
          if (!lat || !lon) {
            console.log('[MapMode] showStopsPosition: skip due to empty lat/lon');
            continue;
          }
          const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
          let bPoint;
          try {
            bPoint = new BMapGL.Point(bdLon, bdLat);
          } catch (e) {
            console.error('[MapMode] showStopsPosition: BMapGL.Point error', e);
            continue;
          }
          const isReturn = stopAction === 'RETURN';
          const iconSize = isReturn ? new BMapGL.Size(20, 20) : new BMapGL.Size(16, 24);
          const iconKey = isReturn ? 'endPoint' : 'stopIcon';
          const rawIconUrl = isReturn ? endPoint : stopIcon;
          const icon = new BMapGL.Icon(rawIconUrl, new BMapGL.Size(
            iconSize.width,
            iconSize.height,
          ), {
            anchor: new BMapGL.Size(iconSize.width / 2, iconSize.height),
          });
          const stopObj = new BMapGL.Marker(bPoint, { icon });
          stopObj.markerType = 3;
          stopObj.markerId = id;
          stopObj.name = name;
          stopObj.vehicleName = vehicleName;
          stopObj.stopAction = stopAction;
          stopObj.goalId = goalId;
          console.log('[MapMode] showStopsPosition: iconUrl=', rawIconUrl);
          try {
            mapRef.current.addOverlay(stopObj);
            console.log('[MapMode] showStopsPosition: addOverlay success for', name, 'zIndex=', stopObj.getZIndex ? stopObj.getZIndex() : 'N/A');
          } catch (e) {
            console.error('[MapMode] showStopsPosition: addOverlay error', e);
          }
          stopObj.setZIndex(15);
          if (stopAction === 'UNLOAD') {
            stopObj.hide();
          }
          markerRef.current.stopMarker[vehicleName].push({
            marker: stopObj,
          });
          stopObj.addEventListener('mouseover', showTipsInfo);
          stopObj.addEventListener('mouseout', hideTipsInfo);
        }
      }
    }
  };

  const hideTipsInfo = () => {
    if (stopTooltipRef.current && mapRef.current) {
      mapRef.current.removeOverlay(stopTooltipRef.current);
      stopTooltipRef.current = null;
    }
  };

  const showTipsInfo = (e: any) => {
    const marker = e.target;
    const { stopAction, name, vehicleName, markerId, goalId } = marker;
    if (stopAction === 'START') {
      return;
    }
    const requestData: RequestOptions = {
      path: '/monitor/web/schedule/stop/detail',
      method: 'POST',
      body: {
        goalId,
        stopId: markerId,
        stopAction,
        vehicleName,
      },
    };

    request(requestData).then((res: HTTPResponse) => {
      const {
        waitingTime,
        totalCollectOrderNum,
        totalDeliveryOrderNum,
        finishedCollectOrderNum,
        finishedDeliveryOrderNum,
        canceledCollectOrderNum,
        canceledDeliveryOrderNum,
        arrivedTime,
        departTime,
      } = res.data;
      let tipsInfo = '';
      if (stopAction === 'LOAD') {
        tipsInfo = `${name}\n点类型：${enumTranslator(
          'STOP_ACTION_ENUM',
          stopAction,
        )}点\n该点应装载：${totalDeliveryOrderNum}\n已装载：${finishedDeliveryOrderNum}\n到达时间：${
          arrivedTime || '--'
        }\n出发时间：${departTime || '--'}\n`;
        if (canceledDeliveryOrderNum) {
          tipsInfo += `订单取消：${canceledDeliveryOrderNum}`;
        }
      } else if (stopAction === 'PICKUP') {
        tipsInfo = `${name}\n点类型：取货点\n已妥投订单：${finishedDeliveryOrderNum}/${totalDeliveryOrderNum}\n${
          totalCollectOrderNum > 0
            ? `已揽收订单：${finishedCollectOrderNum}/${totalCollectOrderNum}\n`
            : ''
        }到达时间：${arrivedTime || '--'}\n出发时间：${departTime || '--'}\n`;
      } else if (stopAction === 'DROPOFF') {
        tipsInfo = `${name}\n点类型：取货点\n${
          totalDeliveryOrderNum > 0
            ? `已妥投订单：${finishedDeliveryOrderNum}/${totalDeliveryOrderNum}\n`
            : ''
        }${
          totalCollectOrderNum > 0
            ? `已揽收订单：${finishedCollectOrderNum}/${totalCollectOrderNum}\n`
            : ''
        }到达时间：${arrivedTime || '--'}\n出发时间：${departTime || '--'}\n`;
      } else if (stopAction === 'UNLOAD') {
        tipsInfo = `${name}\n点类型：${enumTranslator(
          'STOP_ACTION_ENUM',
          stopAction,
        )}点\n该点应卸载：${totalDeliveryOrderNum}\n已卸载：${finishedDeliveryOrderNum}\n到达时间：${
          arrivedTime || '--'
        }\n出发时间：${departTime || '--'}\n`;
      } else if (stopAction === 'RETURN') {
        tipsInfo = `${name}\n到达时间：${arrivedTime || '--'}`;
      }

      // 先清除旧的 tooltip
      hideTipsInfo();

      const { BMapGL } = window as any;
      if (BMapGL && mapRef.current && marker.getPosition) {
        const label = new BMapGL.Label(tipsInfo.replace(/\n/g, '<br>'), {
          position: marker.getPosition(),
          offset: new BMapGL.Size(0, -25),
        });
        label.setStyle({
          backgroundColor: 'rgba(38, 43, 64, 0.9)',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          color: '#fff',
          padding: '6px 10px',
          fontSize: '12px',
          lineHeight: '1.5',
          borderRadius: '4px',
          fontFamily: 'JDZhengHT, PingFangSC, sans-serif',
          boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
          whiteSpace: 'nowrap',
        });
        mapRef.current.addOverlay(label);
        stopTooltipRef.current = label;
      }
    });
  };

  const resetRoutingPathStrokeColor = () => {
    Object.values(markerRef.current.routingPathMarker).forEach((path: any) => {
      if (path) {
        path.forEach((item: any) => {
          item.setStrokeColor('#D3E4FF');
          item.setZIndex(1);
        });
      }
    });

    Object.values(markerRef.current.vehiclePositionMarker).forEach(
      (vehicleMarker: any) => {
        if (vehicleMarker && vehicleMarker.marker) {
          vehicleMarker.marker.setZIndex(1);
        }
      },
    );
  };
  /**
   * 高亮展示routingPath
   * @param {Object} data 包含车号
   */
  const highlightRoutingPathByVehicleName = (data: any) => {
    resetRoutingPathStrokeColor();
    const totalPath = markerRef.current.routingPathMarker[data.vehicleName];
    const vehicleMarker = markerRef.current.vehiclePositionMarker[data.vehicleName];
    const color = '#35FFAB';
    const zIndex = 11;
    if (totalPath) {
      totalPath.forEach((item: any) => {
        item.setStrokeColor(color);
        item.setZIndex(zIndex);
      });
    }

    if (vehicleMarker && vehicleMarker.marker) {
      vehicleMarker.marker.setZIndex(zIndex);
    }

    if (
      markerRef.current.vehiclePositionMarker[data.vehicleName] &&
      markerRef.current.vehiclePositionMarker[data.vehicleName].marker
    ) {
      const { lat, lon } =
        markerRef.current.vehiclePositionMarker[data.vehicleName].marker;
      if (!lat || !lon) return;
      const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
      const { BMapGL } = winAny;
      mapRef.current && mapRef.current.setCenter(new BMapGL.Point(bdLon, bdLat));
    }
  };

  useEffect(() => {
    console.log('[MapMode] init effect running');
    let interval: any;
    const tryInitMap = () => {
      const { BMapGL } = winAny;
      const mapContainer = containerRef.current;
      console.log('[MapMode] tryInitMap: BMapGL=', !!BMapGL, 'container=', !!mapContainer, 'existingMap=', !!mapRef.current);
      if (BMapGL && mapContainer && !mapRef.current) {

        const { lat: bdLat, lon: bdLon } = toBD09(34.53506, 108.93885);
        mapRef.current = init2DMap(
          { lat: bdLat, lon: bdLon },
          mapContainer,
          mapData.zoom || 5,
        );
        console.log('[MapMode] tryInitMap: map init result=', !!mapRef.current);
        if (mapRef.current) {
          clearInterval(interval);
          // 地图初始化完成后，若已有数据则立即重绘所有覆盖物
          //（避免组件被条件卸载后再挂载时，因依赖未变化导致覆盖物不显示）
          showCityPosition();
          showStationPosition();
          showVehicleRealTimePosition();
          showStopsPosition();
          showTotalRoutingPath();
          showPlanningRoutingPath();
          showRoutingPath();
        }
      }
    };

    tryInitMap();
    if (!mapRef.current) {
      interval = setInterval(tryInitMap, 500);
    }

    addGlobalEventListener(
      GlobalEventName.JDX_HIGHLIGHT_ROUTING_PATH,
      highlightRoutingPathByVehicleName,
    );

    return () => {
      clearInterval(interval);
      mapRef.current = null;
      markerRef.current = {
        cityMarker: {},
        routingPathMarker: {},
        planningRoutingPath: {},
        stopMarker: {},
        stationMarker: {},
        vehiclePositionMarker: {},
        finishedRoutingPath: {},
      };
      removeGlobalEventListener(
        GlobalEventName.JDX_HIGHLIGHT_ROUTING_PATH,
        highlightRoutingPathByVehicleName,
      );
    };
  }, []);

  useEffect(() => {
    if (isShowZoom) {
      showZoomInfo();
    }
  }, [isShowZoom]);

  useEffect(() => {
    console.log('[MapMode] mapData.city changed:', mapData.city);
    showCityPosition();
  }, [mapData.city]);

  useEffect(() => {
    console.log('[MapMode] mapData.station changed:', mapData.station);
    showStationPosition();
  }, [mapData.station]);

  useEffect(() => {
    console.log('[MapMode] mapData.vehicle changed:', mapData.vehicle);
    showVehicleRealTimePosition();
  }, [mapData.vehicle]);

  useEffect(() => {
    console.log('[MapMode] mapData.totalRoutingPoint changed:', mapData.totalRoutingPoint);
    showTotalRoutingPath();
  }, [mapData.totalRoutingPoint]);

  useEffect(() => {
    console.log('[MapMode] mapData.currentRoutingPoint changed:', mapData.currentRoutingPoint);
    showPlanningRoutingPath();
  }, [mapData.currentRoutingPoint]);

  useEffect(() => {
    console.log('[MapMode] mapData.stop changed:', mapData.stop);
    showStopsPosition();
  }, [mapData.stop]);

  useEffect(() => {
    console.log('[MapMode] mapData.finishedRoutingPoint changed:', mapData.finishedRoutingPoint);
    showRoutingPath();
  }, [mapData.finishedRoutingPoint]);

  useEffect(() => {
    console.log('[MapMode] mapData.zoom changed:', mapData.zoom);
    mapRef.current && mapRef.current.setZoom(mapData.zoom || 5);
  }, [mapData.zoom]);

  useEffect(() => {
    if (mapData.centerPoint) {
      const [lat, lon] = mapData.centerPoint;
      const { lat: bdLat, lon: bdLon } = toBD09(lat, lon);
      const { BMapGL } = winAny;
      mapRef.current && mapRef.current.setCenter(
        new BMapGL.Point(bdLon, bdLat),
      );
    }
  }, [mapData.centerPoint]);

  return (
    <>
      <div id="route-map" ref={containerRef} />
      {isBeiDou && (
        <span
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            color: '#fff',
          }}
        >
          基于北斗卫星融合定位
        </span>
      )}
    </>
  );
};

export default React.memo(Map);
